import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Container,
    Grid,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Chip from 'components/Chip';
import { BOARD_MEMBER_DATA } from '../../data/board-member-data';

const BoardMember = () => {
    const theme = useTheme();

    const fallbackImage = '/logo-semarang.webp';

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
                backgroundImage: `
                    repeating-linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.04,
                    )} 0px, ${alpha(
                    theme.palette.primary.main,
                    0.04,
                )} 1px, transparent 6px, transparent 30px),
                    radial-gradient(1200px 600px at -10% -30%, ${alpha(
                        theme.palette.primary.light,
                        0.25,
                    )} 0%, transparent 60%),
                    radial-gradient(800px 400px at 110% 130%, ${alpha(
                        theme.palette.primary.main,
                        0.18,
                    )} 0%, transparent 60%),
                    linear-gradient(180deg, ${alpha(
                        theme.palette.primary.main,
                        0.06,
                    )} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)
                `,
                backgroundRepeat: 'repeat, no-repeat, no-repeat, no-repeat',
                backgroundSize: '16px 16px, cover, cover, cover',
                backgroundPosition: 'top left, center, center, center',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 120,
                    background: `linear-gradient(to bottom, ${alpha(
                        theme.palette.primary.main,
                        0.08,
                    )}, transparent)`,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 120,
                    background: `linear-gradient(to top, ${alpha(
                        theme.palette.primary.light,
                        0.08,
                    )}, transparent)`,
                },
            }}
        >
            <Container>
                <Box py={6}>
                    {BOARD_MEMBER_DATA.map((section) => (
                        <Box key={section.section} mt={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                                mb={2}
                            >
                                <Chip label={section.section} />
                            </Box>

                            {section.roles.map((role) => (
                                <Box
                                    key={`${section.section}-${role.position}`}
                                    mb={5}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 800,
                                            mb: 1,
                                            color: theme.palette.text.primary,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {role.position}
                                    </Typography>

                                    <Grid
                                        container
                                        spacing={3}
                                        justifyContent="center"
                                        p={8}
                                    >
                                        {role.member.map((m, idx) => (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                md={4}
                                                lg={3}
                                                key={`${m.name}-${idx}`}
                                            >
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        borderRadius: 4,
                                                        overflow: 'hidden',
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        transition:
                                                            'all 0.3s ease',
                                                        background: `linear-gradient(135deg, ${
                                                            theme.palette
                                                                .primary.main
                                                        } 0%, ${alpha(
                                                            theme.palette
                                                                .secondary.dark,
                                                            0.05,
                                                        )} 100%)`,
                                                        '&:hover': {
                                                            transform:
                                                                'translateY(-6px)',
                                                            boxShadow:
                                                                '0 15px 30px rgba(0,0,0,0.12)',
                                                            borderColor:
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                        },
                                                    }}
                                                >
                                                    {/* Simple decorative header instead of image */}
                                                    <CardMedia
                                                        component="img"
                                                        image={
                                                            m.image ||
                                                            fallbackImage
                                                        }
                                                        alt={m.name}
                                                        className="member-image"
                                                        sx={{
                                                            padding: m.image
                                                                ? 0
                                                                : 5,
                                                            objectFit: m.image
                                                                ? 'cover'
                                                                : 'contain',
                                                            opacity: m.image
                                                                ? 1
                                                                : 0.4,
                                                            width: '100%',
                                                            height: {
                                                                xs: 280,
                                                                sm: 300,
                                                                md: 320,
                                                            },
                                                            transition:
                                                                'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        }}
                                                    />

                                                    <CardContent
                                                        sx={{
                                                            px: 3,
                                                            py: 1,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 700,
                                                                mb: 0,
                                                                fontSize:
                                                                    '1.05rem',
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .primary,
                                                                lineHeight: 1.3,
                                                                minHeight: 40,
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                            }}
                                                        >
                                                            {m.name}
                                                        </Typography>

                                                        <Box
                                                            sx={{
                                                                width: 'full',
                                                                height: 3,
                                                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                                                borderRadius: 2,
                                                                mx: 'auto',
                                                            }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default BoardMember;
